import { Translate } from 'i18n';
import React, { useCallback, useMemo } from 'react';
import { AUTH_URL } from 'students/constants';
import emailIconPNG from 'students/views/shared/assets/providers/email/email-logo.png';
import emailIcon from 'students/views/shared/assets/providers/email/email-logo.svg';
import emailIconWebp from 'students/views/shared/assets/providers/email/email-logo.webp';
import smsIconPNG from 'students/views/shared/assets/providers/sms/sms-logo.png';
import smsIcon from 'students/views/shared/assets/providers/sms/sms-logo.svg';
import smsIconWebp from 'students/views/shared/assets/providers/sms/sms-logo.webp';
import Collapsible from 'students/views/shared/components/Collapsible';
import styled from 'styled-components';
import { SocialButton } from '../components';
import { isNordic, isProviderPrimary, socials } from '../helpers';
import { SFaddedText, SHeader } from '../styled';
import { FU_DOMAIN } from '../constants';

interface IProps {
  emailVariantClickHandler: () => void;
  smsVariantClickHandler: () => void;
}

const StartView: React.FC<IProps> = ({
  emailVariantClickHandler,
  smsVariantClickHandler
}) => {
  const isFromFU = window.location.search.includes(FU_DOMAIN);
  const smsIsPrimary = isNordic();

  const [primarySocials, moreSocials] = useMemo(() => {
    const primary = socials.filter((s) => isProviderPrimary(s.visibility));
    const more = socials.filter((s) => !isProviderPrimary(s.visibility));
    return [primary, more];
  }, []);

  const handleSocialClick = useCallback((link?: string) => {
    if (!link) return;
    const popupURL = `${AUTH_URL ?? ''}${link}`;
    window.open(popupURL);
  }, []);

  return (
    <>
      <SHeader className="onboarding-startview-header">
        {isFromFU ? (
          <Translate str="frontend.auth.sign_in_to_proceed" />
        ) : (
          <Translate str="frontend.auth.join_lingu" />
        )}
      </SHeader>

      <SVariants className="onboarding-startview-variants">
        <SOtpVariant
          onClick={emailVariantClickHandler}
          fullNameLocaleKey="frontend.auth.continue_with_email"
          logo={emailIcon}
          webpLogo={emailIconWebp}
          fallbackLogo={emailIconPNG}
        />

        {smsIsPrimary && (
          <SOtpVariant
            onClick={smsVariantClickHandler}
            fullNameLocaleKey="frontend.auth.continue_with_phone"
            logo={smsIcon}
            webpLogo={smsIconWebp}
            fallbackLogo={smsIconPNG}
          />
        )}

        <SFaddedText>
          <Translate str="frontend.auth.or" />
        </SFaddedText>

        {primarySocials.map(({ providerName, link, webpLogo, logo, fallbackLogo }) => (
          <SocialButton
            key={providerName}
            link={link}
            onClick={handleSocialClick}
            providerName={providerName}
            logo={logo}
            webpLogo={webpLogo}
            fallbackLogo={fallbackLogo}
          />
        ))}

        <Collapsible
          triggerButtonContentOnClose={
            <Translate
              str="frontend.auth.show_more_options"
              params={{ defaultValue: 'Show more options' }}
            />
          }
          triggerButtonContentOnOpen={
            <Translate
              str="frontend.auth.hide_more_options"
              params={{ defaultValue: 'Hide more options' }}
            />
          }
          renderItems={() => (
            <SMoreItems>
              {!smsIsPrimary && (
                <SocialButton
                  onClick={smsVariantClickHandler}
                  fullNameLocaleKey="frontend.auth.continue_with_phone"
                  logo={smsIcon}
                  webpLogo={smsIconWebp}
                  fallbackLogo={smsIconPNG}
                />
              )}
              {moreSocials.map(({ providerName, link, webpLogo, logo, fallbackLogo }) => (
                <SocialButton
                  key={providerName}
                  link={link}
                  onClick={handleSocialClick}
                  providerName={providerName}
                  logo={logo}
                  webpLogo={webpLogo}
                  fallbackLogo={fallbackLogo}
                />
              ))}
            </SMoreItems>
          )}
        />
      </SVariants>
    </>
  );
};

export default StartView;

const SVariants = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: center;

  button {
    margin: 0.5rem 0;

    &:hover {
      text-decoration: none;
    }
  }
`;

const SOtpVariant = styled(SocialButton)`
  margin-block-start: 0.5rem;
`;

const SMoreItems = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;
